import { useEffect, useRef, useState } from "react";
import { SCREENS, UrlConstants } from "../utils/CommonUtils/constants";
import * as Sentry from "@sentry/browser";
import LoadingWrapper from "../screens/loadingWrapper/LoadingWrapper";
import StudentCohortResultScreen from "../screens/studentCohortResultScreen/StudentCohortResultScreen";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import getAssessmentSet from "../fetchApi/getAssessmentSet";
import useParamValues from "../utils/useParamValues";
import CustomErrorDialogBox from "../components/CustomErrorDialogBox/CustomErrorDialogBox";
import useTranslationSetup from "../utils/useTranslationSetup";
import DemoResultsScreen from "../screens/DemoResultsScreen/DemoResultsScreen";
import AssessmentNotConductedScreen from "../screens/AssessmentNotConductedScreen/AssessmentNotConductedScreen";

const ResultFlow = () => {
  const { t } = useTranslationSetup();
  const { studentId, token, classNum } = useParamValues();
  const [activeScreen, setActiveScreen] = useState(
    SCREENS.RENDER_STUDENT_COHORT_RESULT_LOADING_SCREEN,
  );
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [assessmentSetResponse, setAssessmentSetResponse] = useState(null);
  const [selectedAssessmentResult, setSelectedAssessmentResult] =
    useState(null);
  const [feedbackGiven, setFeedbackGiven] = useState({
    primary: false,
    secondary: false,
  });
  const assessmentUrl = `${
    UrlConstants.BASE_URL + UrlConstants.BASE_PATH_URL + UrlConstants.ASSESSMENT
  }`;
  const [reload, setReload] = useState(true);
  const abortController = useRef(null);

  async function handleFetchAssessmentSet() {
    setReload(false);
    try {
      const response = await getAssessmentSet(
        `${assessmentUrl}/result/assessment-set?student_id=${studentId}&grade=${classNum}`,
        abortController,
        token,
      );
      setAssessmentSetResponse(response);
    } catch (error) {
      if (error.status === 404) {
        setActiveScreen(SCREENS.RENDER_STUDENT_NO_ASSESSMENT_RESULT_SCREEN);
      }
      Sentry.captureException(error);
      setErrorDialogOpen(true);
    }
  }

  const handleRetryClick = () => {
    setErrorDialogOpen(false);
    setReload(true);
  };

  useEffect(() => {
    if (assessmentSetResponse) {
      const resultData = assessmentSetResponse?.assessments_result?.[0];
      if (resultData) {
        setSelectedAssessmentResult(resultData);
        setActiveScreen(SCREENS.RENDER_STUDENT_ASSESSMENT_RESULT_SCREEN);
      }
    }
  }, [assessmentSetResponse]);

  useEffect(() => {
    if (reload) {
      handleFetchAssessmentSet();
    }
  }, [reload]);

  const handleShowDemoResult = (assessmentId) => {
    const resultData = assessmentSetResponse?.assessments_result?.find(
      (assessment) => assessment.id === assessmentId,
    );
    if (resultData) {
      setSelectedAssessmentResult(resultData);
      setActiveScreen(SCREENS.RENDER_STUDENT_ASSESSMENT_RESULT_SCREEN);
    }
  };

  const handleShowStudentList = () => {
    // For now, we are comenting this line to avoid sending the message to React Native when showing the student list. Uncomment it if you want to send the message.
    // sendMessageToReactNative("status=2");
  };

  const renderActiveScreen = () => {
    switch (activeScreen) {
      case SCREENS.RENDER_STUDENT_COHORT_RESULT_LOADING_SCREEN:
        return (
          <>
            <LoadingWrapper type="resultLoading" />
            <CustomErrorDialogBox
              header={t("something_went_wrong")}
              icon={
                <ErrorOutlineOutlinedIcon
                  style={{
                    color: "#D95959",
                    borderRadius: "10px",
                    marginTop: "16px",
                    width: "80px",
                    height: "80px",
                  }}
                />
              }
              primaryButtonText={t("retake")}
              secondaryButtonText={t("new_student")}
              isDialogOpen={errorDialogOpen}
              showPrimaryButton
              showSecondaryButton={false}
              onClickPrimaryButton={handleRetryClick}
              onClickSecondaryButton={
                () => {}
                // Uncomment the line below if you want to send the message to React Native when showing the student list.
                // sendMessageToReactNative("status=2")
              }
            />
          </>
        );
      case SCREENS.RENDER_STUDENT_COHORT_RESULT_SCREEN:
        return (
          <StudentCohortResultScreen
            assessmentSetResponse={assessmentSetResponse}
            onSelectAssessment={handleShowDemoResult}
          />
        );
      case SCREENS.RENDER_STUDENT_ASSESSMENT_RESULT_SCREEN:
        return (
          <DemoResultsScreen
            processedData={selectedAssessmentResult}
            goBack={() =>
              setActiveScreen(SCREENS.RENDER_STUDENT_COHORT_RESULT_SCREEN)
            }
            feedbackDisabled={
              feedbackGiven[selectedAssessmentResult.assessment_type]
            }
            setFeedbackDisabled={() =>
              setFeedbackGiven((prev) => ({
                ...prev,
                [selectedAssessmentResult.assessment_type]: true, // Disable only this assessment_type
              }))
            }
          />
        );
      case SCREENS.RENDER_STUDENT_NO_ASSESSMENT_RESULT_SCREEN:
        return (
          <AssessmentNotConductedScreen
            handleShowStudentList={handleShowStudentList}
          />
        );
      default:
        break;
    }
  };

  return <>{renderActiveScreen()}</>;
};

export default ResultFlow;
